// Veri Yönetimi › "Bu setten paket oluştur" (DR10): ad ("<set adı> paketi") + sektör (müşteri
// sektöründen `normalizeSectorCode` ön seçili) → `createKpiPackageFromSet`. Setin ortak katalog
// göstergeleri üye olur; müşteriye özel olanlar "önce kataloğa yükseltin" listesinde atlanır.
// Başarıda "Paket oluşturuldu: <ad> (N üye)" + Paketler sayfasına bağlantı.

import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import {
  SECTOR_CODES,
  SECTOR_LABELS,
  normalizeSectorCode,
  type KpiSet,
  type SectorCode,
} from "@mahir/shared";
import { ApiError } from "../../api/client";
import { createKpiPackageFromSet, type KpiPackageFromSetResult } from "../../api/kpi";
import { KPI_PACKAGES_COPY, packageErrorText } from "../../lib/kpi-packages-copy";
import { useKpiPackagesStore } from "../../stores/kpi-packages-store";
import { SearchSelect } from "../common/SearchSelect";
import { KpiModal } from "./KpiModal";
import {
  amberBox,
  errorBox,
  fieldStyle,
  footerBtn,
  inputStyle,
  labelStyle,
} from "./kpi-form-styles";

const C = KPI_PACKAGES_COPY.fromSet;
const SECTOR_OPTIONS = SECTOR_CODES.map((c) => ({ value: c, label: SECTOR_LABELS[c] }));

export interface PackageFromSetDialogProps {
  open: boolean;
  set: KpiSet;
  /** müşteri kaydındaki serbest sektör metni (ön seçim) */
  clientSector?: string | null | undefined;
  onClose: () => void;
}

/** Başarı görünümü (saf — SSR testi). */
export function PackageFromSetDone({ result }: { result: KpiPackageFromSetResult }): JSX.Element {
  const skipped = result.skippedClientMetrics;
  return (
    <div role="status" aria-live="polite">
      <div style={{ fontWeight: 600, color: "var(--color-primary-text)" }}>
        {C.done(result.package.name, result.package.itemCount)}
      </div>
      <Link
        to={`/clients/kpi/paketler?packageId=${result.package.id}`}
        className="text-sm"
        style={{
          color: "var(--color-primary-text)",
          display: "inline-flex",
          minHeight: 44,
          alignItems: "center",
        }}
      >
        {C.open} →
      </Link>
      {skipped.length > 0 ? (
        <div style={{ ...amberBox, marginTop: 8 }}>
          <div style={{ fontWeight: 600 }}>{C.skippedTitle(skipped.length)}</div>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {skipped.map((s) => (
              <li key={s.metricId}>{s.label}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** Hata metni: boş set (KPI_PACKAGE_EMPTY) kendi metniyle, diğerleri paket kopya tablosundan. */
export function fromSetErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    return e.code === "KPI_PACKAGE_EMPTY"
      ? C.emptyError
      : packageErrorText(e.code, e.message, e.details);
  }
  return packageErrorText("", e instanceof Error ? e.message : undefined);
}

export function PackageFromSetDialog(props: PackageFromSetDialogProps): JSX.Element | null {
  const { open, set, clientSector, onClose } = props;
  const ids = { name: useId(), sector: useId() };
  const [name, setName] = useState("");
  const [sector, setSector] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KpiPackageFromSetResult | null>(null);
  const invalidatePackages = useKpiPackagesStore((s) => s.invalidatePackages);

  useEffect(() => {
    if (!open) return;
    setName(C.defaultName(set.name));
    setSector(normalizeSectorCode(clientSector) ?? "");
    setError(null);
    setResult(null);
  }, [open, set.name, clientSector]);

  if (!open) return null;

  async function submit(): Promise<void> {
    if (name.trim() === "" || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createKpiPackageFromSet(set.id, {
        name: name.trim(),
        sectorCode: sector === "" ? null : (sector as SectorCode),
      });
      invalidatePackages();
      setResult(res);
    } catch (e) {
      setError(fromSetErrorText(e));
    } finally {
      setBusy(false);
    }
  }

  const footer = result ? (
    <div className="flex justify-end">
      <button type="button" style={footerBtn(true)} onClick={onClose}>
        {C.close}
      </button>
    </div>
  ) : (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <button type="button" style={footerBtn()} onClick={onClose} disabled={busy}>
        {C.cancel}
      </button>
      <button
        type="button"
        style={footerBtn(true)}
        disabled={busy || name.trim() === ""}
        aria-busy={busy || undefined}
        onClick={() => void submit()}
      >
        {busy ? C.creating : C.create}
      </button>
    </div>
  );

  return (
    <KpiModal
      open
      kicker={KPI_PACKAGES_COPY.page.kicker}
      title={C.title}
      description={C.description}
      onClose={onClose}
      footer={footer}
      width={600}
    >
      {result ? (
        <PackageFromSetDone result={result} />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div style={fieldStyle}>
            <label htmlFor={ids.name} style={labelStyle}>
              {C.name}
            </label>
            <input
              id={ids.name}
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={fieldStyle}>
            <span id={ids.sector} style={labelStyle}>
              {C.sector}
            </span>
            <SearchSelect
              ariaLabel={C.sector}
              value={sector}
              options={SECTOR_OPTIONS}
              onChange={setSector}
              emptyLabel={KPI_PACKAGES_COPY.identity.noSector}
              placeholder={KPI_PACKAGES_COPY.identity.noSector}
              showCode={false}
            />
          </div>
          {error ? (
            <div role="alert" style={errorBox}>
              {error}
            </div>
          ) : null}
        </form>
      )}
    </KpiModal>
  );
}
